import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import SEO from '../../components/SEO';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { supportAPI } from '../../services/supportAPI';
import { CheckCircle, Clock, Send, Paperclip, X, AlertCircle, RefreshCw, FileText, Download, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLORS = {
    'Open': 'bg-blue-100 text-blue-800 border-blue-200',
    'Assigned': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    'In Progress': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Waiting for Customer': 'bg-orange-100 text-orange-800 border-orange-200',
    'Resolved': 'bg-green-100 text-green-800 border-green-200',
    'Closed': 'bg-gray-100 text-gray-800 border-gray-200',
    'Reopened': 'bg-purple-100 text-purple-800 border-purple-200',
};

const getAssetUrl = (path) => {
    if (!path) return '';
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace('/api', '');
    return baseUrl + path;
};

const AttachmentViewer = ({ url, isInternal, isAdmin }) => {
    const isImage = url.match(/\.(jpeg|jpg|gif|png|webp)$/i) != null;
    const fullUrl = getAssetUrl(url);

    if (isImage) {
        return (
            <a href={fullUrl} target="_blank" rel="noopener noreferrer" className={`block mt-2 overflow-hidden rounded-lg border ${isInternal ? 'border-amber-200' : 'border-midnight-100'} hover:opacity-90 transition-opacity`}>
                <img src={fullUrl} alt="Attachment" className="max-w-full max-h-48 object-cover" />
            </a>
        );
    }

    return (
        <a href={fullUrl} target="_blank" rel="noopener noreferrer" 
           className={`flex items-center gap-1.5 px-3 py-2 mt-2 rounded-lg text-xs font-semibold ${
               isInternal ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' 
               : isAdmin ? 'bg-white/20 text-white hover:bg-white/30' 
               : 'bg-midnight-50 text-midnight-700 hover:bg-midnight-100'
           }`}>
            <FileText className="w-4 h-4" />
            View Document
        </a>
    );
};

const SupportTicketDetail = () => {
    const { id } = useParams();
    const { admin } = useAuth();
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Reply state
    const [replyText, setReplyText] = useState('');
    const [files, setFiles] = useState([]);
    const [isInternalNote, setIsInternalNote] = useState(false);
    const [replying, setReplying] = useState(false);
    const messagesEndRef = useRef(null);

    const isSuperAdmin = admin?.role === 'super_admin';
    const basePath = isSuperAdmin ? '/superadmin/support' : '/admin/support';

    const fetchTicket = async () => {
        setLoading(true);
        try {
            const res = await supportAPI.getTicketDetails(id);
            setTicket(res.data.ticket);
            setMessages(res.data.messages);
            setActivity(res.data.activity);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch ticket');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTicket();
    }, [id]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleFileChange = (e) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            if (files.length + newFiles.length > 3) {
                toast.error('Maximum 3 files allowed');
                return;
            }
            setFiles(prev => [...prev, ...newFiles]);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim() && files.length === 0) return;
        
        setReplying(true);
        try {
            const payload = new FormData();
            payload.append('message', replyText);
            payload.append('isInternalNote', isInternalNote);
            files.forEach(f => payload.append('attachments', f));

            const res = await supportAPI.adminReply(ticket._id, payload);
            setMessages(prev => [...prev, res.data.message]);
            setReplyText('');
            setFiles([]);
            toast.success(isInternalNote ? 'Internal note added' : 'Reply sent');
            
            fetchTicket();
        } catch (err) {
            toast.error('Failed to send reply');
        } finally {
            setReplying(false);
        }
    };

    const handleStatusChange = async (e) => {
        const newStatus = e.target.value;
        try {
            await supportAPI.updateStatus(ticket._id, newStatus);
            toast.success('Status updated');
            fetchTicket();
        } catch (err) {
            toast.error('Failed to update status');
        }
    };

    if (loading) return (
        <AdminLayout>
            <div className="min-h-[60vh] flex items-center justify-center"><LoadingSpinner size="lg" /></div>
        </AdminLayout>
    );

    if (error) return (
        <AdminLayout>
            <div className="p-6 text-center">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">Error Loading Ticket</h2>
                <p className="text-midnight-500 mb-4">{error}</p>
                <Link to={basePath} className="btn-secondary">Back to Tickets</Link>
            </div>
        </AdminLayout>
    );

    return (
        <AdminLayout>
            <SEO title={`Ticket ${ticket.ticketNumber} | Admin`} />
            <div className="p-6 max-w-7xl mx-auto grid lg:grid-cols-3 gap-6">
                
                {/* Main Content - Chat */}
                <div className="lg:col-span-2 flex flex-col h-[85vh]">
                    {/* Header */}
                    <div className="card p-4 mb-4 shrink-0 bg-white">
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <h1 className="text-xl font-display font-bold text-midnight-900 mb-1">
                                    {ticket.subject}
                                </h1>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-mono text-midnight-500">{ticket.ticketNumber}</span>
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border uppercase ${STATUS_COLORS[ticket.status]}`}>
                                        {ticket.status}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Status Changer */}
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-semibold text-midnight-500">Status:</label>
                                <select 
                                    className="input-field py-1.5 px-3 text-sm min-w-[150px]"
                                    value={ticket.status}
                                    onChange={handleStatusChange}
                                >
                                    <option value="Open">Open</option>
                                    <option value="Assigned">Assigned</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Waiting for Customer">Waiting for Customer</option>
                                    <option value="Resolved">Resolved</option>
                                    <option value="Closed">Closed</option>
                                    <option value="Reopened">Reopened</option>
                                </select>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-midnight-100 text-sm">
                            <div>
                                <p className="text-xs text-midnight-400">Customer</p>
                                <p className="font-medium text-midnight-900 truncate">{ticket.customerName}</p>
                            </div>
                            <div>
                                <p className="text-xs text-midnight-400">Category</p>
                                <p className="font-medium text-midnight-900 truncate">{ticket.category}</p>
                            </div>
                            <div>
                                <p className="text-xs text-midnight-400">Station</p>
                                <p className="font-medium text-midnight-900 truncate">{ticket.station?.name || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-midnight-400">Assigned To</p>
                                <p className="font-medium text-midnight-900 truncate">{ticket.assignedTo?.name || 'Unassigned'}</p>
                            </div>
                            {ticket.bookingId && (
                                <div>
                                    <p className="text-xs text-midnight-400">Booking ID / PNR</p>
                                    <p className="font-medium text-midnight-900 truncate">{ticket.bookingId}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Messages Area */}
                    <div className="card flex-grow overflow-hidden flex flex-col">
                        <div className="flex-grow overflow-y-auto p-4 space-y-4 bg-slate-50">
                            {messages.map((msg) => {
                                const isAdmin = msg.senderType === 'admin';
                                const isInternal = msg.isInternalNote;

                                return (
                                    <div key={msg._id} className={`flex flex-col ${isAdmin ? 'items-end' : 'items-start'}`}>
                                        <div className="text-xs text-midnight-400 mb-1 px-1 flex items-center gap-1">
                                            {isInternal && <Lock className="w-3 h-3 text-amber-500" />}
                                            {msg.senderName} • {new Date(msg.createdAt).toLocaleString('en-IN', { hour: '2-digit', minute:'2-digit', month: 'short', day: 'numeric' })}
                                        </div>
                                        <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 shadow-sm ${
                                            isInternal 
                                                ? 'bg-amber-50 border border-amber-200 text-amber-900 rounded-tr-none' 
                                            : isAdmin 
                                                ? 'bg-midnight-600 text-white rounded-tr-none' 
                                            : 'bg-white border border-midnight-100 text-midnight-800 rounded-tl-none'
                                        }`}>
                                            {isInternal && <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600 mb-1 border-b border-amber-200/50 pb-1">Internal Note (Hidden from customer)</div>}
                                            <p className="whitespace-pre-wrap text-sm">{msg.message}</p>
                                            
                                            {msg.attachments?.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {msg.attachments.map((url, i) => (
                                                        <AttachmentViewer key={i} url={url} isInternal={isInternal} isAdmin={isAdmin} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Reply Box */}
                        <div className="p-4 border-t border-midnight-100 bg-white">
                            <form onSubmit={handleReply}>
                                <div className="flex gap-4 mb-2">
                                    <label className="flex items-center gap-2 text-sm text-midnight-600 cursor-pointer">
                                        <input type="radio" name="replyType" checked={!isInternalNote} onChange={() => setIsInternalNote(false)} className="accent-railway-500" />
                                        Reply to Customer
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-amber-600 cursor-pointer font-medium">
                                        <input type="radio" name="replyType" checked={isInternalNote} onChange={() => setIsInternalNote(true)} className="accent-amber-500" />
                                        <Lock className="w-3 h-3" /> Add Internal Note
                                    </label>
                                </div>
                                
                                <div className={`flex gap-2 rounded-lg border-2 overflow-hidden transition-colors ${isInternalNote ? 'border-amber-200 focus-within:border-amber-400 bg-amber-50/30' : 'border-midnight-200 focus-within:border-railway-500 bg-white'}`}>
                                    <textarea 
                                        value={replyText}
                                        onChange={e => setReplyText(e.target.value)}
                                        placeholder={isInternalNote ? "Type an internal note for staff (customer won't see this)..." : "Type your reply to the customer..."}
                                        className="flex-grow resize-none p-3 outline-none text-sm bg-transparent"
                                        rows="2"
                                    />
                                </div>
                                
                                {files.length > 0 && (
                                    <div className="mt-2 flex gap-2 flex-wrap">
                                        {files.map((file, i) => (
                                            <div key={i} className="flex items-center gap-1 bg-midnight-50 px-2 py-1 rounded text-xs border border-midnight-100">
                                                <span className="truncate max-w-[100px]">{file.name}</span>
                                                <button type="button" onClick={() => setFiles(fs => fs.filter((_, idx) => idx !== i))}><X className="w-3 h-3 text-red-500"/></button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="mt-3 flex justify-between items-center">
                                    <label className="cursor-pointer flex items-center gap-2 text-midnight-500 hover:text-railway-500 transition-colors">
                                        <Paperclip className="w-5 h-5" />
                                        <span className="text-sm">Attach files</span>
                                        <input type="file" multiple className="hidden" onChange={handleFileChange} accept=".jpg,.png,.pdf" />
                                    </label>
                                    
                                    <button type="submit" disabled={replying || (!replyText.trim() && files.length === 0)} className={`btn-primary py-2 px-5 flex items-center gap-2 ${isInternalNote ? 'bg-amber-500 hover:bg-amber-600 focus:ring-amber-500' : ''}`}>
                                        {replying ? 'Sending...' : <><Send className="w-4 h-4" /> {isInternalNote ? 'Save Note' : 'Send Reply'}</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Sidebar - Timeline */}
                <div className="lg:col-span-1 h-full">
                    <div className="card p-5 h-full flex flex-col">
                        <h3 className="font-display font-bold text-lg text-midnight-900 mb-4 flex items-center gap-2 shrink-0">
                            <Clock className="w-5 h-5 text-midnight-400" />
                            Activity Log
                        </h3>
                        
                        <div className="flex-grow overflow-y-auto pr-2">
                            <div className="space-y-4">
                                {activity.map((act) => (
                                    <div key={act._id} className="relative pl-6 before:absolute before:inset-0 before:left-[11px] before:w-0.5 before:bg-midnight-100 pb-2">
                                        <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-midnight-50 border-2 border-white flex items-center justify-center text-midnight-400 shadow-sm z-10">
                                            <div className="w-2 h-2 rounded-full bg-railway-400" />
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-midnight-900">{act.action}</div>
                                            {act.oldValue && act.newValue && (
                                                <div className="text-xs text-midnight-600 mt-0.5">
                                                    {act.oldValue} → <span className="font-medium text-midnight-800">{act.newValue}</span>
                                                </div>
                                            )}
                                            <div className="text-[10px] text-midnight-400 mt-1 uppercase tracking-wider font-semibold">
                                                {new Date(act.createdAt).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} 
                                                {act.performedBy?.name && ` • by ${act.performedBy.name}`}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </AdminLayout>
    );
};

export default SupportTicketDetail;
